import { typedObjectKeys, type ContextContent } from "$root/utils";
import ACROutlookExpected from "$root/widgets-architecture/widgets/ACROutlookExpected";
import AssetClassOverviewBlock from "$root/widgets-architecture/widgets/AssetClassOverviewBlock";
import Attribution from "$root/widgets-architecture/widgets/Attribution";
import CommentaryMDBlock from "$root/widgets-architecture/widgets/CommentaryMDBlock";
import Composition from "$root/widgets-architecture/widgets/Composition";
import ContributionVolatility from "$root/widgets-architecture/widgets/ContributionVolatility";
import CurrentRegimeCounterBlock from "$root/widgets-architecture/widgets/CurrentRegimeCounterBlock";
import CustomOutlookBlock from "$root/widgets-architecture/widgets/CustomOutlookBlock";
import EvolutionOfRegimeProbabilitiesBlock from "$root/widgets-architecture/widgets/EvolutionOfRegimeProbabilitiesBlock";
import ExanteContributionVolatility from "$root/widgets-architecture/widgets/ExanteContributionVolatility";
import ExposureCompare from "$root/widgets-architecture/widgets/ExposureCompare";
import ExposureEvolve from "$root/widgets-architecture/widgets/ExposureEvolve";
import Forecast from "$root/widgets-architecture/widgets/Forecast";
import MarketRegimeAnalysisBlock from "$root/widgets-architecture/widgets/MarketRegimeAnalysisBlock";
import OutlookBlock from "$root/widgets-architecture/widgets/OutlookBlock";
import PastMarketRegimesBlock from "$root/widgets-architecture/widgets/PastMarketRegimesBlock";
import PortfolioHistoryBlock from "$root/widgets-architecture/widgets/PortfolioHistoryBlock";
import PortfolioMonitoringBlock from "$root/widgets-architecture/widgets/PortfolioMonitoringBlock";
import PortfolioPerformance from "$root/widgets-architecture/widgets/PortfolioPerformance/PortfolioPerformance";
import Positioning from "$root/widgets-architecture/widgets/Positioning";
import RegimeBreakdownBySectorBlock from "$root/widgets-architecture/widgets/RegimeBreakdownBySectorBlock";
import RegimeTransitionMatrix1MBlock from "$root/widgets-architecture/widgets/RegimeTransitionMatrix1MBlock";
import RegimeTransitionMatrix2WBlock from "$root/widgets-architecture/widgets/RegimeTransitionMatrix2WBlock";
import RegimeTransitionMatrix3MBlock from "$root/widgets-architecture/widgets/RegimeTransitionMatrix3MBlock";
import ReturnAnalysis from "$root/widgets-architecture/widgets/ReturnAnalysis";
import ScenarioAnalysisEditable from "$root/widgets-architecture/widgets/ScenarioAnalysisEditable";
import ScenarioDescriptionBlock from "$root/widgets-architecture/widgets/ScenarioDescriptionBlock";
import React, { type CSSProperties, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { WidgetBlockContext } from "./context";
import { reportPlatformError } from "$root/api/error-reporting";
import { IconWalls } from "$root/components/IconWall";
import BenchmarkComposition from "$root/widgets-architecture/widgets/BenchmarkComposition";
import BondsDividendsTracker from "$root/widgets-architecture/widgets/BondsDividendsTracker";
import ExanteMetricsBlock from "$root/widgets-architecture/widgets/ExanteMetricsBlock";
import FactorsExposure from "$root/widgets-architecture/widgets/FactorsExposure";
import MaturityCouponTracker from "$root/widgets-architecture/widgets/MaturityCouponTracker";
import PerformanceMetricsBlock from "$root/widgets-architecture/widgets/PortfolioMetrics";
import PortfolioCommentaryMDBlock from "$root/widgets-architecture/widgets/PortfolioCommentaryMDBlock";
import PortfolioFactorsExposure from "$root/widgets-architecture/widgets/PortfolioFactorsExposure";
import WorldGridMapBlock from "$root/widgets-architecture/widgets/WorldGridMap";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { ErrorBoundary } from "@mdotm/shadcn/legacy/components";
import { Card } from "../Card";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";

export const widgetMap = {
	BenchmarkComposition,
	ScenarioAnalysisEditable,
	Positioning,
	OutlookBlock,
	AssetClassOverviewBlock,
	CommentaryMDBlock,
	Forecast,
	ReturnAnalysis,
	EvolutionOfRegimeProbabilitiesBlock,
	Composition,
	PortfolioPerformance,
	ACROutlookExpected,
	RegimeTransitionMatrix2WBlock,
	RegimeTransitionMatrix1MBlock,
	RegimeTransitionMatrix3MBlock,
	MarketRegimeAnalysisBlock,
	PastMarketRegimesBlock,
	RegimeBreakdownBySectorBlock,
	ScenarioDescriptionBlock,
	CurrentRegimeCounterBlock,
	ExposureEvolve,
	ExposureCompare,
	MaturityCouponTracker,
	BondsDividendsTracker,
	CustomOutlookBlock,
	Attribution,
	ContributionVolatility,
	ExanteContributionVolatility,
	PortfolioHistoryBlock,
	PortfolioCommentaryMDBlock,
	PerformanceMetricsBlock,
	ExanteMetricsBlock,
	FactorsExposure,
	PortfolioFactorsExposure,
	PortfolioMonitoringBlock,
	WorldGridMapBlock,
};

export const allWidgetNames = typedObjectKeys(widgetMap);

export type WidgetMapperProps = {
	widgetName: keyof typeof widgetMap;
	style?: CSSProperties;
	span?: 1 | 2 | 3;
};

const noIds: ReadonlySet<string> = new Set();

/** The ids of the publishers currently reporting on one channel. The empty set is shared so an idle card never re-renders for it. */
function useIdSet(): [ids: ReadonlySet<string>, setMember: (id: string, on: boolean) => void] {
	const [ids, setIds] = useState<ReadonlySet<string>>(noIds);
	const setMember = useCallback((id: string, on: boolean) => {
		setIds((prev) => {
			if (prev.has(id) === on) {
				return prev;
			}
			const next = new Set(prev);
			if (on) {
				next.add(id);
			} else {
				next.delete(id);
			}
			return next;
		});
	}, []);
	return [ids, setMember];
}

export const WidgetBlock = React.forwardRef<
	HTMLDivElement,
	{
		children?: React.ReactNode;
		style?: CSSProperties;
		widgetName: string;
		span?: 1 | 2 | 3;
	}
>(function WidgetCreate({ children, style, widgetName, span }, ref) {
	const [widgetOptions, setWidgetOptions] = useState<
		Parameters<ContextContent<typeof WidgetBlockContext>["setWidgetOptions"]>[0]
	>({
		actionHeader: null,
		title: "",
	});
	const [loadingIds, setWidgetLoading] = useIdSet();
	const [walledIds, setWidgetWalled] = useIdSet();
	const baseBlockStyle = useMemo(() => ({ ...style, ...widgetOptions.style }), [style, widgetOptions.style]);
	const widgetBlockCtx = useMemo(
		() => ({ setWidgetOptions, setWidgetLoading, setWidgetWalled }),
		[setWidgetLoading, setWidgetWalled],
	);
	// `anyWidgetLoading` gates the action header, `isLoading` the spinner. A `useWidgetLoading` caller
	// publishes its header from inside the query base, which unmounts on the way back to reading and
	// cannot withdraw it; an options-object publisher withdraws its own.
	//
	// Behind a wall the options object is stale for the same reason, so the id channels alone decide
	// the slot: the spinner follows the loading ids and the actions are withheld.
	const anyWidgetLoading = loadingIds.size > 0;
	const anyWidgetWalled = walledIds.size > 0;
	const isLoading = (!anyWidgetWalled && widgetOptions.isLoading) || anyWidgetLoading;
	return (
		<WidgetBlockContext.Provider value={widgetBlockCtx}>
			<Card
				className={overrideClassName("widget", {
					"fill-height": widgetOptions.fillHeight,
				})}
				style={baseBlockStyle}
				data-span={span ?? 1}
				data-widget-name={widgetName}
				data-max-height={widgetOptions.maxHeight}
				innerRef={ref}
				title={widgetOptions.title}
				subTitle={widgetOptions.subTitle}
				actionHeader={anyWidgetLoading || anyWidgetWalled ? null : widgetOptions.actionHeader}
				tooltip={widgetOptions.tooltip}
				isLoading={isLoading}
			>
				{children}
			</Card>
		</WidgetBlockContext.Provider>
	);
});

function WidgetErrorBoundary(props: {
	children: React.ReactNode;
	style?: CSSProperties;
	widgetName: string;
	span?: 1 | 2 | 3;
	innerRef: React.ForwardedRef<HTMLDivElement>;
}) {
	const { t } = useTranslation();
	return (
		<ErrorBoundary
			onError={(err) =>
				reportPlatformError(err, "ERROR", "global", { message: `rendering Widget "${props.widgetName}"` })
			}
			errorChildren={({ reset }) => (
				<Card
					className="widget"
					data-widget-name={props.widgetName}
					data-span={props.span ?? 1}
					style={props.style}
					title={t("OPS")}
					innerRef={props.innerRef}
					actionHeader={
						<Button size="small" onClick={() => reset()} palette="primary">
							{t("RETRY")}
						</Button>
					}
				>
					<IconWalls.ErrorData opaque />
				</Card>
			)}
		>
			<WidgetBlock ref={props.innerRef} style={props.style} widgetName={props.widgetName} span={props.span}>
				{props.children}
			</WidgetBlock>
		</ErrorBoundary>
	);
}

const WidgetsMapper = React.forwardRef<HTMLDivElement, WidgetMapperProps>(function WidgetGet(
	{ widgetName, style, span },
	ref,
) {
	const widgetContent = useMemo(() => {
		const WidgetContent = widgetMap[widgetName];
		return <WidgetContent />;
	}, [widgetName]);

	return (
		<WidgetErrorBoundary style={style} widgetName={widgetName} innerRef={ref} span={span}>
			{widgetContent}
		</WidgetErrorBoundary>
	);
});

export default WidgetsMapper;
